// Status, source and timing for one execution, plus the retry affordance that
// sits next to the status tag.

import React from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { Button, Flex, P, Popover } from '@decisiv/ui-components';
import Redo from '@decisiv/iconix/lib/components/Redo';
import startCase from 'lodash/startCase';

import { formatTimestamp } from '../../utils/dates';

import StatusTag from './StatusTag';
import { MetaRow } from './style';

const EM_DASH = '⁠—';

const Cell = ({ title, children = null, indented = false }) => (
  <Flex flexDirection="column" marginLeft={indented ? 1 : 0}>
    <Flex>
      <P weight="semibold">{title}</P>
    </Flex>
    <Flex>{children}</Flex>
  </Flex>
);

Cell.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  indented: PropTypes.bool,
};
export default function ExecutionMetaRow({ log, logs, retry, onRetried }) {
  const {
    isRetryButtonVisible,
    isRetryButtonDisabled,
    handleRetry,
    retryExecutionIdRef,
  } = retry;

  return (
    <MetaRow>
      <Cell indented title={<Trans>STATUS</Trans>}>
        <StatusTag status={log?.status} />
        {isRetryButtonVisible(log) && (
          <Popover
            placement="bottom"
            manageEvents="hover"
            target={({ ref }) => (
              <Button
                icon={Redo}
                ref={ref}
                aria-label={t`Retry`}
                disabled={isRetryButtonDisabled(logs, log.x_decisiv_trace_id)}
                loading={retryExecutionIdRef.current === log.id}
                variant="ghost"
                style={{
                  cursor: 'pointer',
                  marginLeft: '5px',
                  marginTop: '-6px',
                }}
                onClick={() =>
                  handleRetry(log.id, log.configured_integration_id, onRetried)
                }
              />
            )}
          >
            <Flex padding={1}>
              <span>{t`Retry`}</span>
            </Flex>
          </Popover>
        )}
      </Cell>

      <Cell indented title={<Trans>SOURCE</Trans>}>
        <P weight="medium" color="alaskanHusky">
          {' '}
          {log?.source
            ? startCase(log.source.replace(/_/g, ' ').toLowerCase())
            : EM_DASH}
        </P>
      </Cell>

      <Cell indented title={<Trans>STARTED</Trans>}>
        <P weight="medium" color="alaskanHusky">
          {' '}
          {formatTimestamp(log?.start_date) || EM_DASH}{' '}
        </P>
      </Cell>

      <Cell title={<Trans>END TIME</Trans>}>
        <P weight="medium" color="alaskanHusky">
          {' '}
          {log?.stop_date ? formatTimestamp(log.stop_date) : ''}{' '}
        </P>
      </Cell>
    </MetaRow>
  );
}

ExecutionMetaRow.propTypes = {
  log: PropTypes.oneOfType([PropTypes.object]).isRequired,
  logs: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  retry: PropTypes.shape({
    isRetryButtonVisible: PropTypes.func.isRequired,
    isRetryButtonDisabled: PropTypes.func.isRequired,
    handleRetry: PropTypes.func.isRequired,
    retryExecutionIdRef: PropTypes.shape({ current: PropTypes.string })
      .isRequired,
  }).isRequired,
  onRetried: PropTypes.func.isRequired,
};
